import React, {useCallback, useEffect, useState} from "react";
import {Alert, Button, Card, Col, Row, Skeleton} from "antd";
import {useParams} from "react-router-dom";
import {useTranslate} from "../../../hooks/translate.hook";
import {HealthCheckerIssueDetails} from "../../../models/health-checker/health-checker";
import {healthCheckerService} from "../../../services/health-checker/health-checker.service";
import {IssueHeader} from "./issue.header";
import {IssueDetails} from "./issue.details";
import {IssueAssignment} from "./issue.assignment";
import {IssueIncidents} from "./issue.incidents";
import {IssueResolveModal} from "./issue.resolve.modal";
import "./issue.scss";

export const HealthCenterIssue: React.FC = () => {
    const t = useTranslate();
    const {id} = useParams<{id: string}>();
    const issueId = Number(id);
    const issueIdIsValid = Number.isInteger(issueId) && issueId > 0;
    const [issue, setIssue] = useState<HealthCheckerIssueDetails>();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<boolean>(false);
    const [resolveModalOpen, setResolveModalOpen] = useState<boolean>(false);

    const loadIssue = useCallback(async () => {
        if (!issueIdIsValid) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setLoadError(false);

        try {
            setIssue(await healthCheckerService.getIssue(issueId));
        } catch {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, [issueId, issueIdIsValid]);

    useEffect(() => {
        void loadIssue();
    }, [loadIssue]);

    if (!issueIdIsValid) {
        return (
            <Alert
                type="error"
                showIcon
                message={t("HEALTH-CENTER.ISSUE.INVALID-ID", {defaultValue: "Invalid issue ID"})}
            />
        );
    }

    const initialLoading = loading && !issue;

    return (
        <section className="health-center-issue">
            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    message={t("HEALTH-CENTER.ISSUE.LOAD-ERROR", {defaultValue: "Could not load the issue"})}
                    description={t("HEALTH-CENTER.ISSUE.LOAD-ERROR-DESCRIPTION", {
                        defaultValue: "Try loading the issue again.",
                    })}
                    action={(
                        <Button size="small" danger onClick={() => void loadIssue()}>
                            {t("HEALTH-CENTER.ISSUE.RETRY", {defaultValue: "Retry"})}
                        </Button>
                    )}
                />
            ) : null}

            {initialLoading ? (
                <Card>
                    <Skeleton active avatar paragraph={{rows: 8}} />
                </Card>
            ) : null}

            {issue ? (
                <>
                    <IssueHeader
                        issue={issue}
                        loading={loading}
                        onRefresh={loadIssue}
                        onResolve={() => setResolveModalOpen(true)}
                    />
                    <Row gutter={[20, 20]} align="stretch">
                        <Col xs={24} xl={16}>
                            <IssueDetails issue={issue} />
                        </Col>
                        <Col xs={24} xl={8}>
                            <IssueAssignment issue={issue} />
                        </Col>
                    </Row>
                    <IssueIncidents issueId={issue.id} />
                    {resolveModalOpen ? (
                        <IssueResolveModal
                            issue={issue}
                            onCancel={() => setResolveModalOpen(false)}
                            onComplete={loadIssue}
                        />
                    ) : null}
                </>
            ) : null}
        </section>
    );
};
